import React, { useContext, useEffect, useRef, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { Pencil, Trash2, Download, X, Settings, AlertTriangleIcon } from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
// import DeviceOverview from './DeviceOverview';
import DeviceHeatMap from './DeviceHeatMap';
import '../../styles/pages/device-card.css';
import '../../styles/pages/DeviceDetailPage.css';
import { DevicesContext } from '../../contexts/DevicesContext';
import { DeviceDashboardDto } from '../../services/deviceApi';
//import HelperFunctions from '../../utils/helperFunctions';
// import { useNavigate } from 'react-router-dom';
import DeleteDeviceModal from '../../components/modals/DeleteDeviceModal';
import AQILearningModal from '../../components/modals/AQILearningModal';
// import { useExport } from '../../hooks/useExport';
import { ExportDataTypes, options, getDeviceStatusLabel } from '../../utils/helperFunctions';
import DeviceOverviewHighcharts from './DeviceOverviewHighcharts';
import DevicePredection from './DevicePredection';
import { useAuth } from '../../hooks/useAuth';
import isEqual from 'lodash/isEqual';
import { getUserTimezone } from '../../utils/timezoneUtils';
import DeviceOffSets from './DeviceOffSets';
import DeviceConfigFlowMobile from './DeviceConfigFlowMobile';
import DeviceConfigFlowWeb from './configFlowWeb/DeviceConfigFlowWeb';
import { isRNApp } from '../../utils/helperFunctions';
import { UserSelectedContext } from '../../contexts/UserSelectedContext';
import StatusMessage, { StatusType } from '../../components/StatusMessage';

const DeviceDetailPage: React.FC = () => {
  const shouldShowForecast =
    (process.env.REACT_APP_SHOULD_SHOW_FORECAST ||
      process.env.SHOULD_SHOW_FORECAST ||
      'true') === 'true';
  const { user } = useAuth();
  const userPreferences = JSON.parse(
    localStorage.getItem('userPreferences') || '{}'
  );
  const timeZone = userPreferences.timezone || getUserTimezone();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isDeleteDeviceModalOpen, setIsDeleteDeviceModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeletingSuccess, setIsDeletingSuccess] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [isAQILearningModalOpen, setIsAQILearningModalOpen] = useState(false);
  const [isUpdateOffsetsModalOpen, setIsUpdateOffsetsModalOpen] = useState(false);
  const [showOtherOptions, setShowOtherOptions] = useState(false);
  // Status message state
  const [statusMessage, setStatusMessage] = useState<{
    type: StatusType;
    message: string;
    isVisible: boolean;
  }>({
    type: 'success',
    message: '',
    isVisible: false,
  });
  // const [viewModeHeatmap, setViewModeHeatmap] = useState('currentmonth');

  //device inactive warning message state
  const [showWarning, setShowWarning] = useState(true);

  // This allows mobile users to go directly to change wifi config
  const [showConfigFlow, setShowConfigFlow] = useState(false);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 860);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 860);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // const [deviceDetails, setDeviceDetails]=useState<DeviceDashboardDto | null>(null);
  const devicesContext = useContext(DevicesContext);
  if (!devicesContext) {
    throw new Error('useDevices must be used within a DevicesProvider');
  }
  const {
    devicesForDashboard,
    getDevicesForDashboard,
    getAqiInfoData,
    parametersForDashboard,
    getParametersForDashboard,
    exportDeviceData,
    deviceSensorData,
    deviceHeatmapData,
    deleteDevice,
    getDeviceOffsetsData,
    demoDeviceId,
    demoUserId
  } = devicesContext;
  // const [viewMode, setViewMode] = useState('overview');

  //Get bottom bar state and hide if we using mobile on page reload
  const userSelectedContext = useContext(UserSelectedContext);
    if (!userSelectedContext) {
    throw new Error('useDevices must be used within a DevicesProvider');
  }

  const { hideBottomBar, isBottomBarHidden } = userSelectedContext;

    /*Hide bottom bar on mount (mobile only) and restore it on unmount
      so navigating away doesn't leave the next page without a footer.*/
    useEffect(() => {
      if(window.innerWidth <= 768 && !isBottomBarHidden) {
        hideBottomBar();
      }
    }, []);

  // Get devices and params if not available
  useEffect(() => {
    if (devicesForDashboard.length <= 0 || parametersForDashboard.length <= 0) {
      getDevicesForDashboard();
      getParametersForDashboard();
      getAqiInfoData();
    }
  }, []);

  useEffect(() => {
    if (id) {
      getDeviceOffsetsData(id)
    }
  }, [])

  // Helper function to show status messages
  const showStatusMessage = (type: StatusType, message: string) => {
    setStatusMessage({
      type,
      message,
      isVisible: true,
    });
  };

  // Close status message
  const closeStatusMessage = () => {
    setStatusMessage(prev => ({ ...prev, isVisible: false }));
  };


  const previousDeviceRef = useRef<DeviceDashboardDto | null>(null);

  const deviceDetails = useMemo(() => {
    const found = devicesForDashboard.find(
      (d: DeviceDashboardDto) => d.deviceId === Number(id)
    );

    if (
      !previousDeviceRef.current ||
      !isEqual(previousDeviceRef.current, found)
    ) {
      previousDeviceRef.current = found || null;
    }

    return previousDeviceRef.current;
  }, [devicesForDashboard, id]);

  const parametersForDevice = useMemo(() => {
    if (!deviceDetails) return [];
    const deviceParameterids = Object.keys(deviceDetails.parameters);
    return parametersForDashboard.filter(parameter =>
      deviceParameterids.includes(parameter.driverID.toString())
    );
  }, [deviceDetails, parametersForDashboard]);

  // WiFi status state - 'warning' for REGISTERED+UNKNOWN, 'connected' for WIFICONNECTED+ONLINE, 'offline' for WIFICONNECTED+OFFLINE, null for others
  // const [wifiStatusType, setWifiStatusType] = useState<'warning' | 'connected' | 'offline' | null>(null);

  // useEffect(() => {
  //   const fetchWiFiStatus = async () => {
  //     if (deviceDetails?.serialNumber) {
  //       try {
  //         const { deviceApi } = await import('../../services/deviceApi');
  //         const deviceInfo = await deviceApi.getDeviceBySerialNumber(deviceDetails.serialNumber);
  //         const onboardingStatus = (deviceInfo as any).onboarding_status || deviceInfo.onboardingStatus;
  //         const operationalStatus = (deviceInfo as any).operational_status || (deviceInfo as any).operationalStatus;

  //         if (onboardingStatus !== undefined && onboardingStatus !== null) {
  //           const onboardingUpper = onboardingStatus.toUpperCase();
  //           const operationalUpper = operationalStatus ? operationalStatus.toUpperCase() : 'UNKNOWN';

  //           // Show WiFi warning icon when REGISTERED + UNKNOWN (Wi-Fi not configured)
  //           if (onboardingUpper === 'REGISTERED' && operationalUpper === 'UNKNOWN') {
  //             setWifiStatusType('warning');
  //           }
  //           // Show WiFi warning icon when WIFICONNECTED + UNKNOWN (waiting for data/status unknown)
  //           else if (onboardingUpper === 'WIFICONNECTED' && operationalUpper === 'UNKNOWN') {
  //             setWifiStatusType('warning');
  //           }
  //           // Show regular WiFi icon when WIFICONNECTED + ONLINE (Internet connected)
  //           else if (onboardingUpper === 'WIFICONNECTED' && operationalUpper === 'ONLINE') {
  //             setWifiStatusType('connected');
  //           }
  //           // Show WiFi-off icon when WIFICONNECTED + OFFLINE (Wi-Fi lost / no internet)
  //           else if (onboardingUpper === 'WIFICONNECTED' && operationalUpper === 'OFFLINE') {
  //             setWifiStatusType('offline');
  //           }
  //           else {
  //             setWifiStatusType(null);
  //           }
  //         } else {
  //           setWifiStatusType(null);
  //         }
  //       } catch (error) {
  //         console.error('Error fetching device WiFi status:', error);
  //         setWifiStatusType(null);
  //       }
  //     }
  //   };
  //   fetchWiFiStatus();
  // }, [deviceDetails?.serialNumber]);


  // Memoize AQI calculations to prevent re-renders
  // const aqiData = useMemo(() => {
  //   if (!deviceDetails) return { value: null, status: 'Not Available', color: '#000' };

  //   const aqiParameter = Object.values(deviceDetails.parameters || {}).find(
  //     (value: any) => value.parameterName === 'AQI'
  //   );

  //   return {
  //     value: aqiParameter?.value,
  //     status: HelperFunctions.GetStatusText(aqiParameter?.value, 'AQI') || 'Not Available',
  //     color: HelperFunctions.GetParameterColor(aqiParameter?.value, 'AQI'),
  //     backgroundColor: HelperFunctions.GetAQIBackgroundColor(aqiParameter?.value)
  //   };
  // }, [deviceDetails]);

  useEffect(() => {
    if (window.innerWidth <= 768) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Element;
      if (!target.closest('.export-dropdown-container')) {
        setShowExportDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDeleteDevice = async () => {
    console.log('handleDeleteDevice', id);
    try {
      setIsDeleting(true);
      const response = await deleteDevice(id as string);
      if (response.success) {
        setIsDeletingSuccess(true);
        setIsDeleting(false);
        setTimeout(() => {
          setIsDeleteDeviceModalOpen(false);
          navigate('/devices');
        }, 1500);
      } else {
        console.error('Error deleting device', response);
        setIsDeleting(false);
      }
    } catch (error) {
      console.error('Error deleting device', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleExport = async (fileType: string) => {
    const currentView = (searchParams.get('view') as 'overview' | 'heatmap' | 'predection') ?? 'overview';
    const hasOverviewData = deviceSensorData !== null && deviceSensorData.readings.length > 0;
    const hasHeatmapData = Array.isArray(deviceHeatmapData) && deviceHeatmapData.length > 0;
    const hasDataForCurrentView =
      currentView === 'heatmap' ? hasHeatmapData : hasOverviewData;

    if (!deviceDetails || !id || !hasDataForCurrentView) {
      console.error('Device details or ID not available or no data found for the selected date range');
      showStatusMessage('warning', 'No data available to download for the selected date range. Please try a different period.');
      setShowExportDropdown(false);
      return;
    }

    try {
      console.log('Exporting device data:', {
        deviceId: id,
        fileType: fileType,
      });
      const response = await exportDeviceData(
        id,
        fileType,
        deviceDetails?.serialNumber || '',
        deviceDetails?.deviceName || ''
      );
      if (response?.success) {
        showStatusMessage('success', response?.message || 'Report downloaded successfully');
      } else {
        const msg = (response?.message || '').toLowerCase();
        const isNoData = !msg || /no\s+data|empty|not\s+found|no\s+records|no\s+entries/.test(msg);
        if (isNoData) {
          showStatusMessage('warning', 'No data available to download for the selected date range. Please try a different period.');
        } else {
          showStatusMessage('error', response?.message || 'Reports download failed. Please try again');
        }
      }
      setShowExportDropdown(false);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  // const handleExportDropdownClick = (
  //   e: React.MouseEvent<HTMLButtonElement>
  // ) => {
  //   e.stopPropagation();
  //   setShowExportDropdown(!showExportDropdown);
  // };

  const [searchParams, setSearchParams] = useSearchParams();
  const viewParam = searchParams.get("view") as "overview" | "heatmap" | "predection";
  const tabParam = searchParams.get("tab") as "currentmonth" | "currentyear"

  const viewMode = viewParam ?? "overview";
  const heatmapTab = tabParam ?? "currentmonth";


  // view and tab selection
  useEffect(() => {
    if (!viewParam) {
      setSearchParams(prev => {
        const params = new URLSearchParams(prev);
        params.set("view", viewMode);
        return params;
      }, { replace: true });
    };
  }, []);

  useEffect(() => {
    if (!shouldShowForecast && viewMode === 'predection') {
      setSearchParams({ view: 'overview' }, { replace: true });
    }
  }, [shouldShowForecast, viewMode, setSearchParams]);

  useEffect(() => {
    if (!tabParam && viewMode == "heatmap") {
      setSearchParams(prev => {
        const params = new URLSearchParams(prev);
        params.set("tab", heatmapTab);
        return params;
      }, { replace: true });
    }
  }, []);
  

  // changing view based on user selection 
  const toggleView = (view: "overview" | "heatmap" | "predection") => {
    if (view === "overview") {
      setSearchParams({ view }, { replace: true })
    }else{
      setSearchParams(prev => {
        const params = new URLSearchParams(prev);
        params.set("view", view);
        return params;
      }, { replace: true });
    }
  }

  //changign heat map tab based on user selection from month to year or year to month
  const toggleTab = (tab: "currentmonth" | "currentyear") => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      params.set("tab", tab);
      return params;
    }, { replace: true });
  }

  // ------------------------------
  // 1. Load from URL → update state
  // ------------------------------
  // useEffect(() => {
  //   const view = searchParams.get("view");
  //   const tab = searchParams.get("tab");

  //   // Missing view → set default
  //   if (!view) {
  //     setSearchParams({ view: "overview" }, { replace: true });
  //     return;
  //   }

  //   // Update parent mode
  //   if (view !== viewMode) {
  //     setViewMode(view);
  //     return; // stop here; next effect will sync URL if needed
  //   }

  //   // If view = heatmap → sync tab
  //   if (view === "heatmap") {
  //     if (tab) {
  //       if (tab !== viewModeHeatmap) setViewModeHeatmap(tab);
  //     } else {
  //       // default for heatmap when tab missing
  //       setSearchParams(
  //         { view: "heatmap", tab: "currentmonth" }, { replace: true }
  //       );
  //     }
  //   }
  // }, [searchParams]);

  // useEffect(() => {
  //   const view = searchParams.get("view");
  //   const tab = searchParams.get("tab");

  //   // If URL already correct → no action
  //   if (view === viewMode && (view !== "heatmap" || tab === viewModeHeatmap)) {
  //     return;
  //   }

  //   // Apply correct URL
  //   if (viewMode === "overview") {
  //     setSearchParams({ view: "overview" }, { replace: true });
  //   } else if (viewMode === "heatmap") {
  //     setSearchParams(
  //       {
  //         view: "heatmap",
  //         tab: viewModeHeatmap || "currentmonth",
  //       }
  //     );
  //   }
  // }, [viewMode, viewModeHeatmap]);


  // Handling in active warning 
  const handleInActiveWarning = () => {
    const inActiveWarning = document.querySelector('.inActive-warning-container');
    inActiveWarning?.classList.add('removeInActiveSlider');
    setTimeout(() => {
      setShowWarning(false)
    }, 1000);
  }

  return (
    <DashboardLayout>
      {showConfigFlow && !isRNApp() && !isMobile && deviceDetails?.serialNumber ? (
        <DeviceConfigFlowWeb
          setShowConfigFlow={setShowConfigFlow}
          SerialNumber={deviceDetails.serialNumber}
          deviceId={id}
        />
      ) : (
        <>
      <div
        className={`device-detail-container ${window.innerWidth <= 768 && isAQILearningModalOpen ? 'hide-on-aqi-learning-modal' : ''}`}
      >
        {/* Header */}
        <div className="device-header">
          <div className="header-left zoomOutLevelForDeviceInfoCard">
            <div className="header-left-container">
              <button
                className="back-button"
                onClick={() => {
                  // window.history.back();
                  navigate(`/devices`);
                }}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="back-icon"
                >
                  <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
                </svg>
                <span className="md:hidden block ml-4 device-title">
                  Device Details
                </span>
              </button>
              {/* Only for mobile view */}
              <div
                className="ellipsis-vertical-icon"
                onClick={() => setShowOtherOptions(true)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="var(--color-text)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="1" />
                  <circle cx="12" cy="5" r="1" />
                  <circle cx="12" cy="19" r="1" />
                </svg>
              </div>
            </div>
            {/* Device Information Card for desktop view */}
            {window.innerWidth > 768 && (
              <div className="device-info-card">
                <div className="device-details-container">
                  <div className="device-image-wrapper">
                    <img
                      src={
                        deviceDetails?.deviceImageUrl ||
                        `${process.env.PUBLIC_URL}/DeviceSetup/device.svg`
                      }
                      alt="Device"
                      className="device-image"
                    />
                  </div>

                  <div className="device-details">
                    <div className="device-name-container">
                      <div className="flex items-center gap-2">
                        <h3 className="device-name">
                          {deviceDetails?.deviceName}
                        </h3>
                      </div>
                      <div className="flex items-center gap-2">
                      {(() => {
                        const { label, variant } = getDeviceStatusLabel(deviceDetails?.isActive ?? false, deviceDetails?.operationalStatus);
                        return <div className={`device-status-badge ${variant}`}>{label}</div>;
                      })()}
                      </div>
                    </div>
                    <span className="device-model">
                      <span className="font-normal">Model: </span>
                      {deviceDetails?.deviceModel}
                    </span>
                    {/* <span className="device-connection ml-5">Wifi</span> */}
                  </div>
                </div>

                {/* <div
                className="device-aqi-value"
                style={{
                  background: aqiData.backgroundColor,
                }}
              >
                <p className="aqi-number">
                  {aqiData.value?.toFixed(0) || '--'}
                </p>
                <p
                  className="aqi-label"
                  style={{
                    color: aqiData.color,
                  }}
                >
                  {aqiData.status}
                </p>
              </div> */}
              </div>
            )}
          </div>

          <div className="header-right zoomOutLevel">
            <div className="header-actions">
              {/* <button
                className="action-button edit-button"
                onClick={() => navigate(`/edit-device/${id}`)}
              >
                <img src={`${process.env.PUBLIC_URL}/DevicePageIcons/Info.svg`} alt="Edit" />
                <span>Edit Device Details</span>
              </button> */}

              {isMobile && deviceDetails?.serialNumber && (
                <button
                  className="action-button"
                  onClick={() => setShowConfigFlow(true)}
                  title="Set up Wi-Fi"
                  aria-label="Set up Wi-Fi"
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/DashboardWelcome/Wifi.svg`}
                    alt="Set up Wi-Fi"
                    className="w-5 h-5"
                  />
                </button>
              )}

              {isMobile && (
              <button
                className="action-button"
                onClick={() => setIsUpdateOffsetsModalOpen(true)}
                title="Update Offsets"
                aria-label="Update Offsets"
              >
                <Settings className="w-5 h-5 text-[var(--color-terms-link)]" />
              </button>
              )}

              {user?.username !== 'Demo2' && (
                <button
                  className="action-button"
                  onClick={() => setIsAQILearningModalOpen(true)}
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/DevicePageIcons/Info.svg`}
                    alt="AQI Details"
                  />
                </button>
              )}


              <div className="export-dropdown-container">
                <button
                  className={`action-button ${showExportDropdown ? 'active' : ''}`}
                  onClick={() => setShowExportDropdown(!showExportDropdown)}
                >
                  <Download className="w-5 h-5 text-[var(--color-terms-link)]" />
                </button>
                {showExportDropdown && (
                  <div className="export-dropdown">
                    {ExportDataTypes.filter(
                      type => type.value !== 'pdf' && type.value !== 'png'
                    ).map(type => (
                      <div
                        key={type.value}
                        className="export-dropdown-item"
                        onClick={() => handleExport(type.value)}
                      >
                        <img
                          src={`${process.env.PUBLIC_URL}/SVGIcons/${type.label}.svg`}
                          alt={type.label}
                        />
                        <p>{type.label}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Device Information Card for mobile view */}
        {window.innerWidth <= 768 && (
          <div className="device-info-card">
            <div className="device-details-container">
              <div className="device-image-wrapper">
                <img
                  src={
                    deviceDetails?.deviceImageUrl ||
                    `${process.env.PUBLIC_URL}/DeviceSetup/device.svg`
                  }
                  alt="Device"
                  className="device-image"
                />
              </div>

              <div className="device-details">
                <div className="device-name-container">
                  <div className="flex items-center gap-2">
                    <h3 className="device-name">{deviceDetails?.deviceName}</h3>
                  </div>
                  <div className="flex items-center gap-2">
                    {(() => {
                      const { label, variant } = getDeviceStatusLabel(deviceDetails?.isActive ?? false, deviceDetails?.operationalStatus);
                      return <div className={`device-status-badge ${variant}`}>{label}</div>;
                    })()}
                  </div>
                </div>
                <span className="device-model">
                  {/* <span className="block font-normal">Model Number</span> */}
                  Model: {deviceDetails?.deviceModel}
                </span>
                {/* <span className="device-connection ml-5">Wifi</span> */}
              </div>
            </div>

            {/* <div
                  className="device-aqi-value"
                  style={{
                    background: aqiData.backgroundColor,
                  }}
                >
                  <p className="aqi-number">
                    {aqiData.value?.toFixed(0) || '--'}
                  </p>
                  <p
                    className="aqi-label"
                    style={{
                      color: aqiData.color,
                    }}
                  >
                    {aqiData.status}
                  </p>
                </div> */}
          </div>
        )}

        {/* View Toggle */}
        <div className="flex items-center justify-between zoomOutLevel view-toggle-container">
          <div className="view-toggle">
            <button
              onClick={() => toggleView('overview')}
              className={`toggle-button ${viewMode === 'overview' ? 'active' : ''}`}
            >
              Overview
            </button>
            <button
              onClick={() => toggleView('heatmap')}
              className={`toggle-button ${viewMode === 'heatmap' ? 'active' : ''}`}
            >
              Heat Map
            </button>
            {shouldShowForecast && (
              <button
                onClick={() => toggleView('predection')}
                className={`toggle-button ${viewMode === 'predection' ? 'active' : ''}`}
              >
                Forecast
              </button>
            )}
          </div>
          <div className="last-updated">
            <span>Last updated on :</span>{" "}
            {deviceDetails?.lastUpdated != null
              ? new Date(deviceDetails?.lastUpdated).toLocaleString('en-US', {
                timeZone: timeZone,
                ...options,
              })
              : 'N/A'}
          </div>
        </div>

        {/* Dynamic Content Based on View Mode */}
        {viewMode === 'overview' ? (
          <div className="overview-section zoomOutLevel">
            <DeviceOverviewHighcharts
              parametersForDevice={parametersForDevice}
              liveDeviceData={deviceDetails || ({} as DeviceDashboardDto)}
            />
          </div>
        ) : viewMode === 'heatmap' ? (
          <div className="heatmap-section zoomOutLevel">
            <DeviceHeatMap
              deviceId={deviceDetails?.deviceId?.toString() || ''}
              deviceName={deviceDetails?.deviceName || ''}
              viewMode={heatmapTab}
              setViewMode={toggleTab}
            />
          </div>
        ) : shouldShowForecast ? (
          <div className="overview-section zoomOutLevel">
            <DevicePredection
              parametersForDevice={parametersForDevice}
              liveDeviceData={deviceDetails || ({} as DeviceDashboardDto)}
            />
          </div>
        ) : (
          <div className="overview-section zoomOutLevel">
            <DeviceOverviewHighcharts
              parametersForDevice={parametersForDevice}
              liveDeviceData={deviceDetails || ({} as DeviceDashboardDto)}
            />
          </div>
        )}

        {window.innerWidth <= 768 && !deviceDetails?.isActive && deviceDetails?.lastUpdated && showWarning && (
          <div className='inActive-warning-container'>
            <AlertTriangleIcon className='inActive-warning-icon' />
            <div className="inActive-warning-info">
              <h3 className="inActive-warning-title">Device Offline</h3>
              <p className="inActive-warning-subtitle">Updated on {" "}
                {deviceDetails?.lastUpdated != null ? new Date(deviceDetails?.lastUpdated).toLocaleString('en-US', {
                  timeZone: timeZone,
                  ...options,
                }) : 'N/A'
                }</p>
            </div>
            <button 
              className='inActive-warning-close-btn' 
              onClick={handleInActiveWarning}
              aria-label="Dismiss warning"
            >
              <X className='inActive-warning-remove-icon' />
            </button>
          </div>
        )}
      </div>

      {showOtherOptions && window.innerWidth <= 768 && (
        <div
          className="mobile-action-menu-overlay"
          onClick={() => setShowOtherOptions(false)}
        >
          <div className="mobile-action-menu">
            <button
              className="mobile-action-close"
              onClick={() => setShowOtherOptions(false)}
            >
              <X className="w-5 h-5 text-[var(--color-text)]" />
            </button>
            <div className="mobile-action-content">
              <button
                className="mobile-action-item"
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowOtherOptions(false);
                  setIsAQILearningModalOpen(true);
                }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10 15C10.2833 15 10.521 14.904 10.713 14.712C10.905 14.52 11.0007 14.2827 11 14V10C11 9.71667 10.904 9.47933 10.712 9.288C10.52 9.09667 10.2827 9.00067 10 9C9.71733 8.99933 9.48 9.09533 9.288 9.288C9.096 9.48067 9 9.718 9 10V14C9 14.2833 9.096 14.521 9.288 14.713C9.48 14.905 9.71733 15.0007 10 15ZM10 7C10.2833 7 10.521 6.904 10.713 6.712C10.905 6.52 11.0007 6.28267 11 6C10.9993 5.71733 10.9033 5.48 10.712 5.288C10.5207 5.096 10.2833 5 10 5C9.71667 5 9.47933 5.096 9.288 5.288C9.09667 5.48 9.00067 5.71733 9 6C8.99933 6.28267 9.09533 6.52033 9.288 6.713C9.48067 6.90567 9.718 7.00133 10 7ZM10 20C8.61667 20 7.31667 19.7373 6.1 19.212C4.88334 18.6867 3.825 17.9743 2.925 17.075C2.025 16.1757 1.31267 15.1173 0.788001 13.9C0.263335 12.6827 0.000667933 11.3827 1.26582e-06 10C-0.000665401 8.61733 0.262001 7.31733 0.788001 6.1C1.314 4.88267 2.02633 3.82433 2.925 2.925C3.82367 2.02567 4.882 1.31333 6.1 0.788C7.318 0.262667 8.618 0 10 0C11.382 0 12.682 0.262667 13.9 0.788C15.118 1.31333 16.1763 2.02567 17.075 2.925C17.9737 3.82433 18.6863 4.88267 19.213 6.1C19.7397 7.31733 20.002 8.61733 20 10C19.998 11.3827 19.7353 12.6827 19.212 13.9C18.6887 15.1173 17.9763 16.1757 17.075 17.075C16.1737 17.9743 15.1153 18.687 13.9 19.213C12.6847 19.739 11.3847 20.0013 10 20Z"
                    fill="#A2A5C1"
                  />
                </svg>
                About AQI
              </button>
              <button
                className="mobile-action-item"
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowOtherOptions(false);
                  setShowExportDropdown(true);
                }}
              >
                <Download className="w-5 h-5 text-[var(--color-sub-title-text)]" />
                Export Data
              </button>
              {(demoDeviceId != Number(id) || demoUserId == user?.userId) && (
                <>
                  <button
                    className="mobile-action-item"
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowOtherOptions(false);
                      navigate(`/edit-device/${id}`);
                    }}
                  >
                    <Pencil className="w-5 h-5 text-[var(--color-sub-title-text)]" />
                    Edit Device Details
                  </button>
                  <button
                    className="mobile-action-item text-[#E67E7D]"
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowOtherOptions(false);
                      setIsDeleteDeviceModalOpen(true);
                    }}
                  >
                    <Trash2 className="w-5 h-5 text-[#E67E7D]" />
                    Delete Device
                  </button>
                  <button
                    className="mobile-action-item"
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowOtherOptions(false);
                      setIsUpdateOffsetsModalOpen(true);
                    }}
                  >
                    <Settings className="w-5 h-5 text-[var(--color-sub-title-text)]" />
                    Update Offsets
                  </button>
                  <button
                    className="mobile-action-item"
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowOtherOptions(false);
                      setShowConfigFlow(true);
                    }}
                  >
                    <Settings className="w-5 h-5 text-[var(--color-sub-title-text)]" />
                    Wi-Fi Connectivity
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showExportDropdown && window.innerWidth <= 768 && (
        <div
          className="mobile-modal-overlay"
          onClick={() => setShowExportDropdown(false)}
        >
          <div className="mobile-modal">
            <div className="mobile-modal-header">
              <h3 className="mobile-modal-title">Select Export Type</h3>
              <X
                className="w-5 h-5 text-[var(--color-text)]"
                onClick={() => setShowExportDropdown(false)}
              />
            </div>
            <div className="mobile-modal-content">
              {ExportDataTypes.filter(
                type => type.value === 'excel' || type.value === 'csv'
              ).map(type => (
                <div
                  key={type.value}
                  className="mobile-modal-option"
                  onClick={() => handleExport(type.value)}
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/SVGIcons/${type.label}.svg`}
                    alt={type.label}
                  />
                  <p>{type.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <DeleteDeviceModal
        isOpen={isDeleteDeviceModalOpen}
        onClose={() => setIsDeleteDeviceModalOpen(false)}
        onConfirm={() => handleDeleteDevice()}
        isDeleting={isDeleting}
        isDeletingSuccess={isDeletingSuccess}
      />

      <AQILearningModal
        isOpen={isAQILearningModalOpen}
        onClose={() => setIsAQILearningModalOpen(false)}
      />

      {isUpdateOffsetsModalOpen && (
        <DeviceOffSets
          isOpen={isUpdateOffsetsModalOpen}
          onClose={() => setIsUpdateOffsetsModalOpen(false)}
          deviceDetails={deviceDetails || ({} as DeviceDashboardDto)}
        />
      )}

      {showConfigFlow && (isRNApp() || isMobile) && deviceDetails?.serialNumber && (
        <DeviceConfigFlowMobile setShowConfigFlow={setShowConfigFlow} SerialNumber={deviceDetails?.serialNumber || ''} deviceId={id} />
      )}

      {/* Status Message */}
      <StatusMessage
        type={statusMessage.type}
        message={statusMessage.message}
        isVisible={statusMessage.isVisible}
        onClose={closeStatusMessage}
        autoHideDelay={4000} // 4 seconds
      />
        </>
      )}
    </DashboardLayout>
  );
};

export default DeviceDetailPage;
